import React, {
  useContext,
  createContext,
  useMemo,
  useState,
  Dispatch,
  SetStateAction,
} from 'react';
import { User } from '../../models/user.model';

export interface AuthState {
  authenticated?: boolean;
  user?: User;
}

type SetAuthState = Dispatch<SetStateAction<AuthState>>;

type AuthContentProps = [AuthState, SetAuthState];

// Define the default context state
const initialValues: AuthState = {
  authenticated: false,
  user: new User(),
};

// Create the context with proper type
const AuthContent = createContext<AuthContentProps | undefined>(undefined);

// Create method to use context
const AuthContext = () => {
  const context = useContext(AuthContent);
  if (!context) {
    throw new Error(`AuthContext must be used within an AuthProvider`);
  }
  const [auth, setAuth] = context;

  const setAuthenticated = (user?: User) => {
    setAuth(auth => ({
      ...auth,
      authenticated: true,
      user,
    }));
  };

  const clearAuth = () => {
    setAuth({ ...initialValues });
  };

  return {
    ...auth,
    setAuthenticated,
    clearAuth,
  };
};

// Create the context provider
const AuthProvider: React.FC = ({ children }) => {
  const [auth, setAuth] = useState<AuthState>(initialValues);
  const value = useMemo(() => [auth, setAuth] as AuthContentProps, [auth]);
  return <AuthContent.Provider value={value}>{children}</AuthContent.Provider>;
};

export { AuthProvider, AuthContext };
